/**
 * Sync lifecycle hook.
 *
 * The controller state (current status snapshot, running job, SSE stream) is
 * held once per app at the <SyncProvider> level. Components reach it via
 * ``useSyncController()`` — all consumers share the same instance, so a sync
 * completion updates the top-bar label, the mode dropdown, and the progress
 * bar together. Having a single SSE subscription also avoids fan-out to N
 * EventSource connections.
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { getSyncStatus, setDataMode, startSync, syncEventsUrl } from '../api';
import { describePhase, PHASES } from '../sync-phases';
import type {
  DataMode,
  PhaseProgress,
  PhaseStateRow,
  SyncJob,
  SyncLogEntry,
  SyncStatus,
} from '../types';

export type PhaseProgressMap = Record<string, PhaseProgress>;

function emptyPhaseProgress(): PhaseProgress {
  return {
    status: 'pending',
    records_total: null,
    records_done: 0,
    records_written: null,
    elapsed_ms: null,
    eta_ms: null,
    started_at: null,
    finished_at: null,
    skipped_age_hours: null,
    sub_phases: [],
    error: null,
  };
}

/** Initialise the phase map with one entry per known descriptor (status: pending). */
function buildEmptyPhaseMap(): PhaseProgressMap {
  const out: PhaseProgressMap = {};
  for (const p of PHASES) {
    out[p.name] = emptyPhaseProgress();
  }
  return out;
}

/**
 * Seed last-known per-phase row counts from /api/sync/status.phase_state.
 *
 * Multiple phase_state rows may map to a single descriptor (e.g.
 * fetch_variables has cat_item + variable_set entries). We aggregate
 * row_count across all matching entries.
 */
function seedFromPhaseState(rows: PhaseStateRow[] | undefined): PhaseProgressMap {
  const map = buildEmptyPhaseMap();
  if (!rows) return map;
  for (const r of rows) {
    const desc = describePhase(r.phase);
    if (!desc) continue;
    const cur = map[desc.name];
    const total = (cur.records_total ?? 0) + (r.row_count ?? 0);
    map[desc.name] = {
      ...cur,
      records_total: total,
      // Don't flip to "completed" from cache — that would lie about the
      // current run's status. We only show the count as a hint.
    };
  }
  return map;
}

/**
 * Reducer for per-phase state. Returns a new map; the input is not mutated.
 *
 * Maps every relevant SSE event to a delta on the descriptor's PhaseProgress
 * row. Dynamic sub-phases (e.g. fetch_slas[P2P]) collapse onto their parent
 * descriptor (fetch_slas) — we record the sub-phase name in `sub_phases`.
 */
function applyPhaseEvent(
  prev: PhaseProgressMap,
  ev: Record<string, unknown>,
): PhaseProgressMap {
  // Terminal events don't carry a phase — they wrap up whatever phase
  // was last running. The orchestrator emits `phase_start finish_job`
  // but never `phase_end finish_job`; `sync_events.finish()` sends
  // `done`/`failed` instead. Without this, Phase 25 spins forever.
  if (ev.type === 'done' || ev.type === 'failed') {
    const out: PhaseProgressMap = { ...prev };
    const terminal = ev.type === 'done' ? 'completed' : 'failed';
    for (const [name, p] of Object.entries(prev)) {
      if (p.status === 'running') {
        out[name] = { ...p, status: terminal, finished_at: Date.now() };
      }
    }
    return out;
  }
  const phase = typeof ev.phase === 'string' ? ev.phase : null;
  if (!phase) return prev;
  const desc = describePhase(phase);
  if (!desc) return prev;
  const cur = prev[desc.name] ?? emptyPhaseProgress();
  const next: PhaseProgress = { ...cur, sub_phases: [...cur.sub_phases] };
  if (phase !== desc.name && !next.sub_phases.includes(phase)) {
    next.sub_phases.push(phase);
  }

  const t = ev.type;
  if (t === 'phase_start') {
    next.status = 'running';
    if (next.started_at == null) next.started_at = Date.now();
    next.error = null;
  } else if (t === 'phase_total') {
    if (typeof ev.records_total === 'number') {
      // For multi-call phases (e.g. fetch_variables), accumulate totals across
      // sub-fetches as they arrive.
      next.records_total = (cur.records_total ?? 0) + ev.records_total;
    }
  } else if (t === 'progress') {
    if (typeof ev.records_done === 'number') next.records_done = ev.records_done;
    if (typeof ev.records_total === 'number') next.records_total = ev.records_total;
    if (typeof ev.elapsed_ms === 'number') next.elapsed_ms = ev.elapsed_ms;
    if (typeof ev.eta_ms === 'number' || ev.eta_ms === null) {
      next.eta_ms = typeof ev.eta_ms === 'number' ? ev.eta_ms : null;
    }
  } else if (t === 'phase_end') {
    next.status = 'completed';
    next.finished_at = Date.now();
    if (typeof ev.records_written === 'number') {
      next.records_written = (cur.records_written ?? 0) + ev.records_written;
    }
    if (typeof ev.elapsed_ms === 'number') next.elapsed_ms = ev.elapsed_ms;
  } else if (t === 'phase_skipped') {
    next.status = 'skipped';
    next.finished_at = Date.now();
    if (typeof ev.age_hours === 'number') next.skipped_age_hours = ev.age_hours;
    if (typeof ev.cached_row_count === 'number') {
      next.records_total = (cur.records_total ?? 0) + ev.cached_row_count;
      next.records_done = next.records_total;
    }
  } else if (t === 'failed') {
    next.status = 'failed';
    next.finished_at = Date.now();
    if (typeof ev.error === 'string') next.error = ev.error;
  }

  return { ...prev, [desc.name]: next };
}

function emptyJob(id: string, startedAt: string): SyncJob {
  return {
    id,
    status: 'running',
    started_at: startedAt,
    finished_at: null,
    error: null,
    current_phase: null,
    current_step: null,
    steps_total: null,
    table: null,
    table_index: null,
    tables_total: null,
    records_total: null,
    records_done: 0,
    elapsed_ms: null,
    eta_ms: null,
    counts: {},
    total: null,
    done: 0,
  };
}

function applyEvent(prev: SyncJob, ev: Record<string, unknown>): SyncJob {
  const next: SyncJob = { ...prev, counts: { ...prev.counts } };
  const phase = ev.phase;
  if (typeof phase === 'string') next.current_phase = phase;
  if (typeof ev.step === 'number') next.current_step = ev.step;
  if (typeof ev.steps_total === 'number') next.steps_total = ev.steps_total;
  if (typeof ev.table === 'string') next.table = ev.table;
  if (typeof ev.table_index === 'number') next.table_index = ev.table_index;
  if (typeof ev.tables_total === 'number') next.tables_total = ev.tables_total;
  if (typeof ev.records_total === 'number') next.records_total = ev.records_total;
  if (ev.records_total === null) next.records_total = null;
  if (typeof ev.records_done === 'number') next.records_done = ev.records_done;
  if (typeof ev.elapsed_ms === 'number') next.elapsed_ms = ev.elapsed_ms;
  if (typeof ev.eta_ms === 'number') next.eta_ms = ev.eta_ms;
  if (ev.eta_ms === null) next.eta_ms = null;
  if (typeof ev.total === 'number') next.total = ev.total;
  if (typeof ev.done === 'number') next.done = ev.done;
  if (ev.counts && typeof ev.counts === 'object') {
    Object.assign(next.counts, ev.counts as Record<string, number>);
  }
  if (ev.type === 'done') {
    next.status = 'completed';
    next.finished_at = new Date().toISOString();
  } else if (ev.type === 'failed') {
    next.status = 'failed';
    next.error = typeof ev.error === 'string' ? ev.error : 'Sync failed';
    next.finished_at = new Date().toISOString();
  }
  return next;
}

function summariseEvent(ev: Record<string, unknown>): SyncLogEntry | null {
  const phase = typeof ev.phase === 'string' ? ev.phase : '';
  const t = ev.type;
  if (t === 'phase_start') {
    const tbl = typeof ev.table === 'string' ? ` · ${ev.table}` : '';
    const step = typeof ev.step === 'number' ? `${ev.step}/${ev.steps_total ?? '?'}` : '';
    return {
      ts: Date.now(),
      kind: 'phase_start',
      phase,
      message: `start ${step}${tbl ? ' ' : ''}${tbl}`.trim(),
    };
  }
  if (t === 'phase_end') {
    const written = typeof ev.records_written === 'number' ? ev.records_written : 0;
    const ms = typeof ev.elapsed_ms === 'number' ? `${(ev.elapsed_ms / 1000).toFixed(1)}s` : '';
    return { ts: Date.now(), kind: 'phase_end', phase, message: `done · ${written} rows · ${ms}` };
  }
  if (t === 'warning') {
    const msg = typeof ev.message === 'string' ? ev.message : 'warning';
    return { ts: Date.now(), kind: 'warning', phase, message: msg };
  }
  if (t === 'phase_skipped') {
    const age = typeof ev.age_hours === 'number' ? ev.age_hours : null;
    const rows =
      typeof ev.cached_row_count === 'number' ? ev.cached_row_count : null;
    const sn = typeof ev.sn_table === 'string' ? ev.sn_table : '';
    const inField = typeof ev.in_field === 'string' && ev.in_field ? `.${ev.in_field}` : '';
    const ageLbl = age != null ? `${age.toFixed(1)}h old` : 'cached';
    const rowsLbl = rows != null ? `, ~${rows.toLocaleString()} rows` : '';
    return {
      ts: Date.now(),
      kind: 'skipped',
      phase,
      message: `skipped (${ageLbl}${rowsLbl}) — ${sn}${inField}`,
    };
  }
  return null;
}

export type SyncController = {
  status: SyncStatus | null;
  job: SyncJob | null;
  log: SyncLogEntry[];
  phases: PhaseProgressMap;
  lastError: string | null;
  start: (opts?: { forceFull?: boolean }) => Promise<void>;
  switchMode: (mode: DataMode) => Promise<void>;
  refresh: () => Promise<void>;
  clearLog: () => void;
};

const SyncContext = createContext<SyncController | null>(null);

export function SyncProvider({ children }: { children: ReactNode }) {
  const value = useSyncControllerState();
  return createElement(SyncContext.Provider, { value }, children);
}

export function useSyncController(): SyncController {
  const ctx = useContext(SyncContext);
  if (!ctx) {
    throw new Error(
      'useSyncController must be used inside <SyncProvider>. Wrap the app tree once.',
    );
  }
  return ctx;
}

function useSyncControllerState(): SyncController {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [job, setJob] = useState<SyncJob | null>(null);
  const [log, setLog] = useState<SyncLogEntry[]>([]);
  const [phases, setPhases] = useState<PhaseProgressMap>(buildEmptyPhaseMap);
  const [lastError, setLastError] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);

  const clearLog = useCallback(() => setLog([]), []);

  const refresh = useCallback(async () => {
    try {
      const s = await getSyncStatus();
      setStatus(s);
      if (s.running) {
        setJob(s.running);
      } else {
        setJob(null);
      }
      // Phase map is owned by the SSE stream and the mount-time seed.
      // Re-seeding here would clobber a just-finished run's green ✓s the
      // moment the `done` event fires (which calls refresh()).
    } catch (e) {
      setLastError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const attachSse = useCallback(
    (jobId: string) => {
      esRef.current?.close();
      const es = new EventSource(syncEventsUrl(jobId));
      esRef.current = es;

      es.addEventListener('snapshot', (raw) => {
        try {
          const snap = JSON.parse((raw as MessageEvent).data) as SyncJob;
          setJob(snap);
        } catch {
          /* ignore */
        }
      });

      es.onmessage = (raw) => {
        try {
          const ev = JSON.parse(raw.data) as Record<string, unknown>;
          setJob((prev) =>
            applyEvent(prev ?? emptyJob(jobId, new Date().toISOString()), ev),
          );
          setPhases((prev) => applyPhaseEvent(prev, ev));
          const entry = summariseEvent(ev);
          if (entry) {
            setLog((prev) => {
              const next = [...prev, entry];
              return next.length > 200 ? next.slice(-200) : next;
            });
          }
          if (ev.type === 'done' || ev.type === 'failed') {
            es.close();
            esRef.current = null;
            if (ev.type === 'failed' && typeof ev.error === 'string') {
              setLastError(ev.error);
            }
            void refresh();
          }
        } catch {
          /* ignore parse errors */
        }
      };

      es.onerror = () => {
        // EventSource auto-reconnects. A terminal error (e.g. backend crashed)
        // just keeps the last-known job state and surfaces via refresh().
      };
    },
    [refresh],
  );

  const start = useCallback(
    async (opts?: { forceFull?: boolean }) => {
      setLastError(null);
      setLog([]);
      // Reset every phase to pending — the SSE stream will repaint statuses.
      setPhases(buildEmptyPhaseMap());
      try {
        const resp = await startSync(opts?.forceFull ?? false);
        setJob(emptyJob(resp.job_id, resp.started_at));
        attachSse(resp.job_id);
      } catch (e) {
        setLastError(e instanceof Error ? e.message : String(e));
      }
    },
    [attachSse],
  );

  const switchMode = useCallback(
    async (mode: DataMode) => {
      setLastError(null);
      try {
        await setDataMode(mode);
        await refresh();
      } catch (e) {
        setLastError(e instanceof Error ? e.message : String(e));
      }
    },
    [refresh],
  );

  // Initial status + if a job is already running, attach SSE to it.
  // The mount-time seed is the only place the phase map gets pre-populated
  // from phase_state — once SSE starts driving it, we never overwrite it
  // until the next start() (or component remount).
  useEffect(() => {
    void (async () => {
      const current = await getSyncStatus().catch(() => null);
      if (!current) return;
      setStatus(current);
      if (current.running) {
        setJob(current.running);
        attachSse(current.running.id);
      } else {
        setJob(null);
        setPhases(seedFromPhaseState(current.phase_state));
      }
    })();
    return () => {
      esRef.current?.close();
      esRef.current = null;
    };
    // attachSse is stable via useCallback.
  }, [attachSse]);

  return { status, job, log, phases, lastError, start, switchMode, refresh, clearLog };
}
